import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { ConnectionEventsService } from "./connection-events.service";

interface StoredGuestUUID {
  value: string;
  expiry: number;
}

@Injectable({
  providedIn: "root",
})
export class SessionService {
  private readonly guestUUIDIndex = "guestUUID";
  private readonly accessTokenIndex = "access_token";
  private readonly refreshTokenIndex = "refresh_token";
  private readonly accountCreationTokenIndex = "account_creation_token";
  private readonly langIndex = "lang";

  public readonly DEFAULT_LANG = "en";

  constructor(private connectionEventsService: ConnectionEventsService) {
    this.connectionEventsService.logout$.subscribe(() => {
      this.clear();
    });
    this.connectionEventsService.login$.subscribe(({ accessToken, refreshToken, language }) => {
      this.clear();
      this.accessToken = accessToken;
      this.refreshToken = refreshToken;
      this.userLang = language;
    });
  }

  /**
   * Clears all authentication-related data from storage
   */
  public clear(): void {
    localStorage.removeItem(this.guestUUIDIndex);
    localStorage.removeItem(this.accessTokenIndex);
    localStorage.removeItem(this.refreshTokenIndex);
    localStorage.removeItem(this.accountCreationTokenIndex);
  }

  /**
   * Stores guestUUID in local storage
   * @param uuid the uuid
   */
  public set guestUUID(uuid: string) {
    const item: StoredGuestUUID = {
      value: uuid,
      expiry: Date.now() + environment.guestDuration * 24 * 60 * 60 * 1000,
    };

    localStorage.setItem(this.guestUUIDIndex, JSON.stringify(item));
  }

  /**
   * Retrieves guestUUID from local storage
   * @return guestUUID or null if not found
   */
  public get guestUUID(): string | null {
    const raw = localStorage.getItem(this.guestUUIDIndex);

    if (!raw) return null;

    try {
      const item: StoredGuestUUID = JSON.parse(raw);
      const now = Date.now();

      if (now > item.expiry) {
        this.clearGuestUUID();
        return null;
      }

      return item.value;
    } catch {
      this.clearGuestUUID();
      return null;
    }
  }

  /**
   * Removes guestUUID from local storage
   */
  public clearGuestUUID(): void {
    return localStorage.removeItem(this.guestUUIDIndex);
  }

  /**
   * Stores access token in local storage
   * @param token JWT access token
   */
  public set accessToken(token: string) {
    localStorage.setItem(this.accessTokenIndex, token);
  }

  /**
   * Retrieves access token from local storage
   * @return Access token or null if not found
   */
  public get accessToken(): string | null {
    return localStorage.getItem(this.accessTokenIndex);
  }

  /**
   * Stores refresh token in local storage
   * @param token JWT refresh token
   */
  public set refreshToken(token: string) {
    localStorage.setItem(this.refreshTokenIndex, token);
  }

  /**
   * Retrieves refresh token from local storage
   * @return Refresh token or null if not found
   */
  public get refreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenIndex);
  }

  /**
   * Stores account creation token in local storage
   * @param token Account creation token
   */
  public set accountCreationToken(token: string) {
    localStorage.setItem(this.accountCreationTokenIndex, token);
  }

  /**
   * Retrieves account creation token from local storage
   * @return Account creation token or null if not found
   */
  public get accountCreationToken(): string | null {
    return localStorage.getItem(this.accountCreationTokenIndex);
  }

  /**
   * Removes the account creation token from local storage
   */
  public clearAccountCreationToken(): void {
    localStorage.removeItem(this.accountCreationTokenIndex);
  }

  /**
   * Checks if user is authenticated
   * @return True if access token exists
   */
  public get isLoggedIn(): boolean {
    return this.accessToken !== null ? true : false;
  }

  /**
   * Checks if a guest exists (valid or not)
   * @return True if a guest exists
   */
  public get isGuest(): boolean {
    return this.guestUUID !== null ? true : false;
  }

  /**
   * Stores user language in local storage
   * @param lang Language code
   */
  public set userLang(lang: string) {
    localStorage.setItem(this.langIndex, lang);
  }

  /**
   * Retrieves user language from local storage
   * @return Stored language or default language
   */
  public get userLang(): string {
    const lang = localStorage.getItem(this.langIndex);
    return lang ? lang : this.DEFAULT_LANG;
  }
}
